import React, { memo, useCallback, useState } from 'react';
import { Button, DialogFooter, Tabs, useOnboarding } from '@wg/wows-react-uikit';
import { TABS_SIZES, TABS_THEMES } from '@wg/wows-react-uikit/components/Tabs/Tabs';

import { ONBOARDING_DIALOGS } from '~/const/onboarding';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import { isChinaRealm } from '~/helpers/realm';
import useMount from '~/hooks/useMount';

import PatrolsDialogHeader from '~/pages/Patrols/components/PatrolsDialogHeader/PatrolsDialogHeader';
import PatrolsRules from '~/pages/Patrols/components/PatrolsRules/PatrolsRules';

import ScrollShadowContainer from '~/UIKit/ScrollShadowContainer/ScrollShadowContainer';

import styles from './PatrolsInfoDialog.module.scss';
import PromoImage from './images/patrol.png';
import PromoImageCN from './images/patrol_cn.png';

import type { IDialog } from '~/types/declaration';

const isCN = isChinaRealm();

enum PATROLS_INFO_TABS {
  ABOUT = 'about',
  RULES = 'rules',
}

type IPatrolsInfoDialog = IDialog<{
  title?: string;
}>;

const PatrolsInfoDialog: React.FC<IPatrolsInfoDialog> = ({ hideDialog }) => {
  const { completeSteps } = useOnboarding({ stepName: ONBOARDING_DIALOGS.PATROLS_ROUTE_ENTRY });

  const [tabSelected, setTabSelected] = useState<PATROLS_INFO_TABS>(PATROLS_INFO_TABS.ABOUT);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.PATROL_INFO_MODAL.OPEN_MODAL);

    return () => {
      completeSteps([ONBOARDING_DIALOGS.PATROLS_ROUTE_ENTRY]);
    };
  });

  const onTabClick = useCallback(
    (tab: PATROLS_INFO_TABS) => {
      dwhExport.push(DWH_EVENTS.PATROL_INFO_MODAL.TAB_CLICK, { tab });
      setTabSelected(tab);
    },
    [setTabSelected],
  );

  const onButtonClick = useCallback(() => {
    dwhExport.push(DWH_EVENTS.PATROL_INFO_MODAL.BUTTON_CLICK);
    hideDialog();
  }, [hideDialog]);

  const tabs = {
    [PATROLS_INFO_TABS.ABOUT]: {
      value: PATROLS_INFO_TABS.ABOUT,
      content: t('About Patrols'),
      component: (
        <div className={styles.aboutWrapper}>
          <div className={styles.aboutImgWrapper}>
            <img className={styles.aboutImg} src={isCN ? PromoImageCN : PromoImage} width={600} height={600} />
          </div>
          <div className={styles.aboutHeader}>{t('CLAN PATROLS')}</div>
          <div className={styles.aboutDescription}>
            {t(
              'Embark on battle Patrols together with your Clanmates to earn bonuses. Play in Divisions and get additional rewards!',
            )}
          </div>
          <div className={styles.aboutActions}>
            <Button isFlat onClick={onButtonClick}>
              {t('Go to Patrols')}
            </Button>
          </div>
        </div>
      ),
    },
    [PATROLS_INFO_TABS.RULES]: {
      value: PATROLS_INFO_TABS.RULES,
      content: t('Rules'),
      component: (
        <div className={styles.rulesWrapper}>
          <ScrollShadowContainer className={styles.rules}>
            <PatrolsRules />
          </ScrollShadowContainer>
          <DialogFooter>
            <Button isFlat onClick={onButtonClick}>
              {t('Go to Patrols')}
            </Button>
          </DialogFooter>
        </div>
      ),
    },
  };

  return (
    <>
      <PatrolsDialogHeader hideDialog={hideDialog} />
      <div className={styles.header}>
        <Tabs<PATROLS_INFO_TABS>
          tabs={Object.values(tabs)}
          tabSelected={tabSelected}
          theme={TABS_THEMES.CLIENT}
          size={TABS_SIZES.M}
          onToggleTab={onTabClick}
        />
      </div>
      <div className={styles.content}>{tabs[tabSelected].component}</div>
    </>
  );
};

export default memo(PatrolsInfoDialog);
